import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Film, Download, Loader2, CheckCircle2,
  AlertCircle, Play, Clock, Wand2, Home,
} from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";

const API = process.env.REACT_APP_BACKEND_URL;

/** Polling-Intervall (ms) für Sora-2 Job-Status. */
const POLL_MS = 8000;

export default function RecruitingClip() {
  const { user } = useCampusAuth();
  const [presets, setPresets] = useState([]);
  const [quota, setQuota] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedSlogan, setSelectedSlogan] = useState(null);
  const [activeJob, setActiveJob] = useState(null); // {job_id, status, ...}
  const [starting, setStarting] = useState(false);
  const pollTimer = useRef(null);

  // Initial-Load
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [p, q, h] = await Promise.all([
          axios.get(`${API}/api/recruiting-clip/presets`, { withCredentials: true }),
          axios.get(`${API}/api/recruiting-clip/quota`, { withCredentials: true }),
          axios.get(`${API}/api/recruiting-clip/history`, { withCredentials: true }),
        ]);
        if (!alive) return;
        setPresets(p.data.slogans || []);
        setSelectedSlogan((p.data.slogans || [])[0]?.id || null);
        setQuota(q.data);
        setHistory(h.data.items || []);
        // Wenn noch ein Job läuft, direkt weiter pollen
        const running = (h.data.items || []).find((it) =>
          it.status === "queued" || it.status === "rendering"
        );
        if (running) setActiveJob(running);
      } catch {
        toast.error("Konnte Recruiting-Clip-Daten nicht laden.");
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  // Polling wenn ein Job läuft
  const pollStatus = useCallback(async (jobId) => {
    try {
      const { data } = await axios.get(
        `${API}/api/recruiting-clip/status/${jobId}`,
        { withCredentials: true }
      );
      setActiveJob(data);
      if (data.status === "done") {
        toast.success("Dein Recruiting-Clip ist fertig!");
        // Quota + History refreshen
        const [q, h] = await Promise.all([
          axios.get(`${API}/api/recruiting-clip/quota`, { withCredentials: true }),
          axios.get(`${API}/api/recruiting-clip/history`, { withCredentials: true }),
        ]);
        setQuota(q.data);
        setHistory(h.data.items || []);
        return true;
      }
      if (data.status === "failed") {
        toast.error(`Rendering fehlgeschlagen: ${data.error || "unbekannt"}`);
        return true;
      }
    } catch {
      /* noop */
    }
    return false;
  }, []);

  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status !== "queued" && activeJob.status !== "rendering") return;
    let cancelled = false;
    const tick = async () => {
      const done = await pollStatus(activeJob.job_id);
      if (done || cancelled) return;
      pollTimer.current = setTimeout(tick, POLL_MS);
    };
    pollTimer.current = setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [activeJob, pollStatus]);

  const startRender = async () => {
    if (!selectedSlogan) return;
    setStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/recruiting-clip/generate`,
        { slogan_id: selectedSlogan },
        { withCredentials: true }
      );
      setActiveJob(data);
      toast.success("Rendering gestartet · Sora 2 braucht 2-5 Minuten.");
      // Quota refresh
      const q = await axios.get(`${API}/api/recruiting-clip/quota`, { withCredentials: true });
      setQuota(q.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Fehler beim Starten.";
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const isRendering = activeJob && (activeJob.status === "queued" || activeJob.status === "rendering");
  const limitReached = quota && quota.limit !== null && quota.remaining === 0 && !isRendering;

  return (
    <div className="max-w-4xl space-y-10" data-testid="recruiting-clip-page">
      {/* Header */}
      <div>
        <Link
          to="/campus/app"
          className="inline-flex items-center gap-2 text-[13px] text-sand-600 hover:text-sand-800 mb-6"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
          Zurück zum Dashboard
        </Link>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-darkred/10 text-brand-darkred px-2.5 py-0.5 text-[10.5px] font-bold tracking-widest mb-3">
          <Sparkles className="h-3 w-3" /> KI-VIDEO · SORA 2
        </div>
        <h1 className="font-display text-sand-800 text-[2rem] sm:text-[2.4rem] font-semibold leading-[1.1]">
          Viraler <span className="text-terra">Recruiting-Clip.</span>
        </h1>
        <p className="mt-4 text-sand-600 text-[15px] leading-[1.65] max-w-2xl">
          Ein 8-Sekunden-Vertikal-Video (9:16) im Skandi-Look — bereit für Instagram
          Reels, TikTok & LinkedIn. Slogan wählen, Rendering starten, in 2-5 Minuten
          herunterladen.
        </p>
      </div>

      {/* Quota-Karte */}
      {quota && (
        <div
          className="rounded-2xl border border-cream-300 bg-cream-50 p-5 flex items-center justify-between gap-4"
          data-testid="recruiting-clip-quota"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-1">
              Dein Kontingent · {quota.month_key}
            </div>
            <div className="font-display text-sand-800 text-[1.15rem] font-semibold">
              {quota.limit === null
                ? `${quota.used} Clips generiert · Unbegrenzt`
                : `${quota.used} / ${quota.limit} Clip${quota.limit === 1 ? "" : "s"} genutzt`}
            </div>
            <div className="text-[13px] text-sand-600 mt-0.5">
              Tarif: <span className="font-medium">{quota.tier}</span>
            </div>
          </div>
          {quota.limit !== null && (
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-sand-500">
                Verbleibend
              </div>
              <div className="font-display text-brand-darkred text-3xl font-semibold">
                {quota.remaining}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Aktiver Job / Ergebnis */}
      {activeJob && (
        <div
          className={`rounded-3xl border p-6 sm:p-8 ${
            activeJob.status === "done"
              ? "bg-brand-darkred text-cream-50 border-brand-darkred"
              : activeJob.status === "failed"
              ? "bg-peach-50 border-terra/30"
              : "bg-cream-50 border-cream-300"
          }`}
          data-testid="recruiting-clip-active-job"
        >
          {activeJob.status === "done" ? (
            <>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron/25 text-saffron px-2.5 py-0.5 text-[10.5px] font-bold tracking-widest mb-3">
                <CheckCircle2 className="h-3 w-3" /> FERTIG · BEREIT ZUM DOWNLOAD
              </div>
              <h2 className="font-display text-3xl leading-[1.05] mb-2">
                „{activeJob.slogan_text}&ldquo;
              </h2>
              <p className="text-cream-100/85 text-[14px] mb-5">
                8 Sekunden · 9:16 · MP4 · optimiert für Reels & TikTok
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`${API}${activeJob.download_url}`}
                  download
                  data-testid="recruiting-clip-download-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-cream-50 text-brand-darkred px-5 py-2.5 text-[13.5px] font-semibold hover:bg-brand-rose transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Clip herunterladen
                </a>
                {user?.is_admin && (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${API}/api/recruiting-clip/${activeJob.job_id}/set-landing-hero`,
                          {},
                          { withCredentials: true }
                        );
                        toast.success("Clip ist jetzt das Landing-Hero auf dickies-helden.de");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Fehler.");
                      }
                    }}
                    data-testid="recruiting-clip-set-landing-hero"
                    className="inline-flex items-center gap-2 rounded-full bg-saffron text-brand-darkred px-5 py-2.5 text-[13.5px] font-semibold hover:opacity-90 transition-opacity"
                  >
                    <Home className="h-4 w-4" />
                    Als Landing-Hero setzen
                  </button>
                )}
                <button
                  onClick={() => setActiveJob(null)}
                  className="inline-flex items-center gap-2 rounded-full border border-cream-100/40 text-cream-50 px-5 py-2.5 text-[13.5px] hover:bg-cream-100/10"
                >
                  Neuen Clip erstellen
                </button>
              </div>
            </>
          ) : activeJob.status === "failed" ? (
            <>
              <div className="inline-flex items-center gap-1.5 text-terra font-bold text-[11px] uppercase tracking-widest mb-3">
                <AlertCircle className="h-3.5 w-3.5" /> Rendering fehlgeschlagen
              </div>
              <p className="text-sand-700 text-[14px] mb-4">
                {activeJob.error || "Unbekannter Fehler. Bitte erneut versuchen."}
              </p>
              <button
                onClick={() => setActiveJob(null)}
                data-testid="recruiting-clip-retry-btn"
                className="inline-flex items-center gap-2 rounded-full bg-sand-800 text-cream-50 px-5 py-2.5 text-[13.5px] font-medium"
              >
                Erneut versuchen
              </button>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-1.5 text-terra font-bold text-[11px] uppercase tracking-widest mb-3">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {activeJob.status === "queued" ? "In Warteschlange" : "Sora 2 rendert…"}
              </div>
              <h2 className="font-display text-sand-800 text-[1.75rem] font-semibold leading-tight mb-2">
                „{activeJob.slogan_text}&ldquo;
              </h2>
              <p className="text-sand-600 text-[14px] mb-4 max-w-lg">
                Das dauert typischerweise <strong>2 bis 5 Minuten</strong>. Du kannst
                diese Seite verlassen — der Clip erscheint im Verlauf, sobald er
                fertig ist.
              </p>
              <div className="flex items-center gap-2 text-[12.5px] text-sand-500">
                <Clock className="h-3.5 w-3.5" />
                Gestartet: {new Date(activeJob.created_at).toLocaleTimeString("de-DE")}
              </div>
            </>
          )}
        </div>
      )}

      {/* Slogan-Auswahl */}
      {!isRendering && !limitReached && (
        <div>
          <h2 className="font-display text-sand-800 text-[1.4rem] font-semibold leading-tight mb-4">
            Wähle deinen Slogan
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {presets.map((s) => {
              const active = selectedSlogan === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSelectedSlogan(s.id)}
                  data-testid={`recruiting-clip-slogan-${s.id}`}
                  className={`text-left rounded-2xl border p-5 transition-all ${
                    active
                      ? "border-brand-darkred bg-white ring-2 ring-brand-darkred/20"
                      : "border-cream-300 bg-white hover:border-sand-400"
                  }`}
                >
                  <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-sand-500 mb-1">
                    {s.eyebrow}
                  </div>
                  <div className="font-display text-sand-800 text-[1.1rem] font-semibold leading-tight">
                    „{s.text}&ldquo;
                  </div>
                  {active && (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-brand-darkred font-bold uppercase tracking-widest">
                      <CheckCircle2 className="h-3 w-3" /> Ausgewählt
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <button
            onClick={startRender}
            disabled={!selectedSlogan || starting}
            data-testid="recruiting-clip-generate-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 disabled:opacity-50 text-cream-50 px-6 py-3 text-[14px] font-semibold transition-colors"
          >
            {starting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Starte…</>
            ) : (
              <><Wand2 className="h-4 w-4" /> Clip mit Sora 2 rendern</>
            )}
          </button>
          <p className="mt-3 text-[12.5px] text-sand-500 max-w-xl">
            Skandi-Look · zwei Pflegefachkräfte am Fenstertisch · PDL-Buch fliegt ins
            Bild · Slogan endet · warmes Cream/Terra/Bordeaux · 8 Sek · 9:16.
          </p>
        </div>
      )}

      {limitReached && (
        <div className="rounded-2xl border border-terra/30 bg-peach-50 p-6" data-testid="recruiting-clip-limit-reached">
          <div className="inline-flex items-center gap-1.5 text-terra font-bold text-[11px] uppercase tracking-widest mb-2">
            <AlertCircle className="h-3.5 w-3.5" /> Monatslimit erreicht
          </div>
          <p className="text-sand-700 text-[14px] mb-3">
            Dein Tarif <strong>{quota.tier}</strong> erlaubt {quota.limit} Clip
            {quota.limit === 1 ? "" : "s"} pro Monat. Neues Kontingent ab dem 1. des
            Folgemonats — oder upgrade auf <strong>Pflege-KI Team</strong> (5
            Clips/Monat).
          </p>
          <Link
            to="/campus/pro"
            className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-cream-50 px-5 py-2.5 text-[13.5px] font-semibold"
          >
            Upgrade ansehen
          </Link>
        </div>
      )}

      {/* Verlauf */}
      {history.length > 0 && (
        <div>
          <h2 className="font-display text-sand-800 text-[1.4rem] font-semibold leading-tight mb-4">
            Dein Verlauf
          </h2>
          <ul className="divide-y divide-cream-300 border-y border-cream-300">
            {history.map((h) => (
              <li
                key={h.job_id}
                data-testid={`recruiting-clip-history-${h.job_id}`}
                className="py-4 flex items-center justify-between gap-5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] font-bold mb-1">
                    <StatusBadge status={h.status} />
                    <span className="text-sand-500">
                      {new Date(h.created_at).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="font-display text-sand-800 text-[14px] font-semibold truncate">
                    „{h.slogan_text}&ldquo;
                  </div>
                  {user?.is_admin && h.user_email && (
                    <div className="text-[11.5px] text-sand-500 mt-0.5">{h.user_email}</div>
                  )}
                </div>
                {h.status === "done" && h.download_url && (
                  <a
                    href={`${API}${h.download_url}`}
                    download
                    className="shrink-0 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] font-bold text-terra"
                  >
                    <Download className="h-3.5 w-3.5" />
                    MP4
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!activeJob && history.length === 0 && (
        <div className="rounded-2xl border border-dashed border-cream-300 bg-white p-8 text-center">
          <Film className="h-8 w-8 text-sand-400 mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-sand-600 text-[14px]">
            Noch keine Clips generiert. Wähle einen Slogan und starte oben.
          </p>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    queued: { label: "In Warteschlange", cls: "bg-sand-100 text-sand-600" },
    rendering: { label: "Rendert…", cls: "bg-saffron/20 text-terra" },
    done: { label: "Fertig", cls: "bg-brand-darkred/10 text-brand-darkred" },
    failed: { label: "Fehler", cls: "bg-peach-100 text-terra" },
  };
  const m = map[status] || { label: status, cls: "bg-sand-100 text-sand-600" };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.cls}`}>{m.label}</span>;
}
